import WasmController from "react-lib/frameworks/WasmController";
import moment from "moment";
import WalletPatientGetBalance from "issara-sdk/apis/WalletPatientGetBalance_apps_BIL";
import NewReportMedicalFeeDetailView from "issara-sdk/apis/NewReportMedicalFeeDetailView_apps_BIL";
import WalletTransactionList from "issara-sdk/apis/WalletTransactionList_apps_BIL";
import InvoiceItemByModeList from "issara-sdk/apis/InvoiceItemByModeList_apps_BIL";
import InvoiceItemList from "issara-sdk/apis/InvoiceItemList_apps_BIL";

// Interface
import { State as MainState } from "HIS/MainHISInterface";
import { SetProperty } from "../../common/CommonInterface";
import { formatComma } from "react-lib/utils/tsUtils";

let toDate = moment();
let year = parseInt(toDate.format("YYYY")) + 543;

export type Props = {};

export type State = {
  selectedEncounter?: Record<string, any>;
  selectedPatient?: Record<string, any>;
  billReportPrint?: BillReportPrintType;
  // Sequence
  BillingDepositHistorySequence?: BillingDepositHistorySequence;
};

export type BillReportPrintType = {
  open?: boolean;
  task_id?: string;
  status?: string;
  progress?: string;
  error?: string;
};

export type BillingDepositHistorySequence = {
  sequenceIndex?: string | null;
  depositHistory?: DepositHistoryType[];
  receiptNo?: string;
  startDate?: string;
  endDate?: string;
  depositTotal?: string;
  selectDepositHistory?: DepositHistoryType;
};

export type DepositHistoryType = {
  id?: number;
  value?: string;
  deposit_code?: string;
  payment_datetime?: string;
  payment_receipt_no?: string;
  payment_encounter_type?: string;
  payment_cashier?: string;
  payment_receipt_id?: string;
  receiptItem?: ReceiptItem[];
  receiptMode?: ReceiptMode[];
  sumPrice?: {
    price?: string;
    send_claim?: string;
    absorb?: string;
    claimable?: string;
    non_claimable?: string;
    discount?: string;
    payable?: string;
    drg_discount_amount?: string;
  };
};

export type ReceiptItem = {
  mode_code?: string;
  code?: string;
  product?: string;
  edited?: string;
  quantity?: string;
  unitprice?: string;
  price?: string;
  send_claim?: string;
  absorb?: string;
  claimable?: string;
  non_claimable?: string;
  discount?: string;
  payable?: string;
  drg_discount_amount?: string;
  discount_code?: string;
};

export type ReceiptMode = {
  bill_mode__code?: string;
  bill_mode__name?: string;
  price__sum?: string;
  send_claim__sum?: string;
  absorb__sum?: string;
  patient_claim__sum?: string;
  non_claimable__sum?: string;
  discount__sum?: string;
  payable__sum?: string;
};

export const StateInitial: State = {
  BillingDepositHistorySequence: {
    receiptNo: "",
    startDate: `${toDate.format("DD/MM/")}${year}`,
    endDate: `${toDate.format("DD/MM/")}${year}`,
    depositTotal: "0.00",
  },
};

// Sequence
type SeqState = {
  sequence: "BillingDepositHistory";
  restart?: boolean;
  clear?: boolean;
  card?: string;
};

type ActionType =
  | { action: "printReceipt"; receipt: string }
  | { action: "getDepositHistory" }
  | { action: "getDepositHistoryDetail" };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;
type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;
export type SetProp = SetProperty<State & PickedState>;
export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
  divisionDict?: any;
  device?: number;
};

export const DataInitial = {};

type Handler = (controller: WasmController<State, Event, Data, Props>, params?: any) => any;

const INVOICE_ITEM_API = {
  Item: InvoiceItemList.list,
  Mode: InvoiceItemByModeList.get,
};

export const Start: Handler = async (controller, params) => {
  const state = controller.getState();
  if (!state.BillingDepositHistorySequence) return;

  const [
    [walletRes, walletErr, walletNet],
    [depositHistoryRes, depositHistoryErr, depositHistoryNet],
  ] = await Promise.all([
    WalletPatientGetBalance.get({
      params: {
        patient: state.selectedEncounter?.patient_id || state.selectedPatient?.id,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    }),
    WalletTransactionList.list({
      params: {
        patient: state.selectedEncounter?.patient_id || state.selectedPatient?.id,
        tx_type: "PAYMENT",
        start_use_date: toDate.format("YYYY-MM-DD"),
        end_use_date: toDate.format("YYYY-MM-DD"),
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    }),
  ]);

  controller.setState({
    BillingDepositHistorySequence: {
      ...StateInitial.BillingDepositHistorySequence,
      sequenceIndex: "Action",
      depositTotal: walletRes?.balance || "0.00",
      depositHistory: depositHistoryRes?.items || [],
    },
  });
};

export const Action: Handler = async (controller, params) => {
  const state = controller.getState();
  if (!state.BillingDepositHistorySequence) return;

  if (params.action === "printReceipt") {
    const state = controller.getState();

    const report = await NewReportMedicalFeeDetailView.get({
      params: {
        receipt: params.receipt,
        patient_name_lang: "TH",
      },
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
        device: controller.data.device,
      },
    });

    controller.setState({
      billReportPrint: { open: true, task_id: report[0]?.task_id || "" },
    });
  } else if (params.action === "getDepositHistory") {
    const state = controller.getState();

    let startDate = changDate(state.BillingDepositHistorySequence?.startDate);
    let endDate = changDate(state.BillingDepositHistorySequence?.endDate);

    const [depositHistoryRes, depositHistoryErr, depositHistoryNet] =
      await WalletTransactionList.list({
        params: {
          patient: state.selectedEncounter?.patient_id || state.selectedPatient?.id,
          tx_type: "PAYMENT",
          start_use_date: startDate,
          end_use_date: endDate,
          deposit_receipt_no: state.BillingDepositHistorySequence?.receiptNo,
        },
        apiToken: controller.apiToken,
        extra: { division: controller.data.division },
      });

    controller.setState({
      BillingDepositHistorySequence: {
        ...state.BillingDepositHistorySequence,
        depositHistory: depositHistoryRes?.items || [],
      },
    });
  } else if (params.action === "getDepositHistoryDetail") {
    const state = controller.getState();

    const [[itemRes, itemErr, itemNet], [modeRes, modeErr, modeNet]] = await Promise.all([
      ...Object.entries(INVOICE_ITEM_API).map(([key, api]) =>
        api({
          params: {
            receipt: params.selectDeposit?.payment_receipt_id,
          },
          apiToken: controller.apiToken,
        })
      ),
    ]);

    const fieldsToSum = [
      "price",
      "send_claim",
      "absorb",
      "claimable",
      "non_claimable",
      "discount",
      "payable",
      "drg_discount_amount",
    ];

    const totals: Record<string, string> = fieldsToSum.reduce((acc, field) => {
      acc[field] = formatComma(
        itemRes?.items.reduce((sum: number, item: any) => sum + parseFloat(item[field] || "0"), 0)
      );

      return acc;
    }, {} as Record<string, string>);

    let value = (params.selectDeposit?.value || "").replace("-", "");
    
    const data = {
      ...params.selectDeposit,
      value: formatComma(value),
      receiptItem: itemRes?.items || [],
      receiptMode: modeRes?.items || [],
      sumPrice: totals,
    };

    controller.setState({
      BillingDepositHistorySequence: {
        ...state.BillingDepositHistorySequence,
        selectDepositHistory: data,
      },
    });
  }
};

const changDate = (date: any) => {
  let splitDate = date.split("/");
  let year = parseInt(splitDate?.[2]) - 543;

  return `${year}-${splitDate?.[1]}-${splitDate?.[0]}`;
};
